import { Link, useParams } from 'react-router-dom';
import { useSavedReport } from '../../../data/queries/use-saved-report';
import { parseSavedReportSnapshot, type SavedReportSnapshot } from './saved-report-snapshot';
import { useSavedReportFile } from './use-saved-report-file';

/**
 * ONE SAVED REPORT — M10 / S10-05.
 *
 * **THIS SCREEN IS THE IMMUTABILITY GUARANTEE MADE VISIBLE.** Everything it
 * shows comes from the frozen snapshot stored with the report: the Project name
 * as it was, the date printed on the document, the Description as written, the
 * branding as used, the Captures as titled. **It issues no query against
 * `project`, `capture`, `capture_photo` or `company_branding`, and imports no
 * report generator** — which is enforced by a structural test, because a
 * helpful join added later is exactly how history starts drifting.
 *
 * **REOPEN MEANS INSPECT, NOT EDIT.** There is no Edit, no Continue, no Resume.
 * A finished document is finished; a different report is a new report.
 */

export function SavedReportScreen() {
  const { projectId = '', reportId = '' } = useParams();
  const { status, report: row, error } = useSavedReport(reportId);

  const snapshot: SavedReportSnapshot | null = row ? parseSavedReportSnapshot(row.snapshot) : null;

  const file = useSavedReportFile({
    reportId,
    ownerUserId: row?.owner_user_id ?? '',
    projectId: row?.project_id ?? projectId,
    filename: row?.filename ?? 'report.pdf',
  });

  return (
    <main className="cl-screen">
      <p className="project-home-back">
        <Link className="cl-link" to={`/projects/${projectId}/reports`}>
          ‹ Saved reports
        </Link>
      </p>

      {status === 'loading' ? (
        <p className="projects-loading" role="status">
          Loading this saved report…
        </p>
      ) : null}

      {status === 'failed' ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}

      {status === 'ready' && row ? (
        <>
          <h1 className="cl-screen-title">Saved report</h1>
          {/* The historical facts, all from the snapshot. */}
          <p className="saved-report-detail-line">
            <strong>{snapshot?.projectName ?? row.filename}</strong>
          </p>
          <p className="saved-report-detail-line">
            {row.report_date} · {String(row.page_count)} {row.page_count === 1 ? 'page' : 'pages'}
          </p>

          {snapshot?.description ? (
            <p className="saved-report-detail-description">{snapshot.description}</p>
          ) : null}

          {snapshot?.branding ? (
            <p className="saved-report-detail-line">
              {snapshot.branding.companyName}
              {snapshot.branding.email ? ` · ${snapshot.branding.email}` : ''}
              {snapshot.branding.phone ? ` · ${snapshot.branding.phone}` : ''}
            </p>
          ) : null}

          {snapshot ? (
            <p className="saved-report-detail-line">
              {String(snapshot.counts.captures)}{' '}
              {snapshot.counts.captures === 1 ? 'Capture' : 'Captures'} ·{' '}
              {String(snapshot.counts.photos)} {snapshot.counts.photos === 1 ? 'photo' : 'photos'}
            </p>
          ) : null}

          <div className="report-pdf-actions">
            <button
              type="button"
              className="cl-button"
              disabled={file.status === 'working'}
              onClick={() => {
                void file.view();
              }}
            >
              View PDF
            </button>
            <button
              type="button"
              className="cl-button cl-button--quiet"
              disabled={file.status === 'working'}
              onClick={() => {
                void file.share();
              }}
            >
              Share PDF
            </button>
          </div>

          {file.message ? (
            <p className="cl-error report-pdf-message" role="alert">
              {file.message}
            </p>
          ) : null}

          {/* Truthful boundary: this document is finished. */}
          <p className="projects-empty-body report-next-boundary">
            This is the report exactly as it was generated. To change anything, generate a new
            report.
          </p>
        </>
      ) : null}
    </main>
  );
}
